import React, { useEffect, useMemo, useState } from 'react';
import { Check, Loader2, ShieldCheck, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../../../app/providers/AuthProvider';
import { authFetch } from '../../../shared/api/authFetch';

const SCOPE_LABELS = {
  'lineside:read': 'Read your permitted LineSide operational context',
  'lineside:author': 'Author governed LineSide configuration in Sandboxes',
  'lineside:execute': 'Run permitted LineSide actions and programs',
  'lineside:publish:request': 'Request review of an exact Sandbox revision',
  'lineside:publish:automatic': 'Publish validated revisions automatically when you enable it for this connection',
  'lineside:approvals:review': 'Collect your explicit approval decisions through a supported client',
  'lineside:initiatives': 'Author and manage permitted Initiatives',
  'lineside:communications': 'Use permitted communications capabilities',
  'lineside:integrations': 'Use permitted integration capabilities',
  'lineside:admin': 'Use permitted tenant administration capabilities',
};

const McpAuthorizationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, initialized } = useAuth();
  const requestToken = useMemo(
    () => new URLSearchParams(location.search).get('request') || '',
    [location.search]
  );
  const [request, setRequest] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [agentId, setAgentId] = useState('');

  useEffect(() => {
    if (!initialized || isAuthenticated) return;
    navigate('/login', {
      replace: true,
      state: { returnTo: `${location.pathname}${location.search}` },
    });
  }, [initialized, isAuthenticated, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!initialized || !isAuthenticated || !requestToken) return;
    let active = true;
    authFetch(`/oauth/consent/request?request=${encodeURIComponent(requestToken)}`)
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || 'This authorization request is invalid or expired.');
        if (active) { setRequest(payload); setAgentId(payload.agent_id || ''); }
      })
      .catch((fetchError) => {
        if (active) setError(fetchError.message || 'Unable to load the authorization request.');
      });
    return () => { active = false; };
  }, [initialized, isAuthenticated, requestToken]);

  const decide = async (approved) => {
    setSubmitting(true);
    setError('');
    try {
      const response = await authFetch('/oauth/consent/decision', {
        method: 'POST',
        body: JSON.stringify({ request: requestToken, approved, agent_id: agentId || null }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.redirect_url) {
        throw new Error(payload.error || 'The authorization decision could not be completed.');
      }
      window.location.assign(payload.redirect_url);
    } catch (decisionError) {
      setError(decisionError.message || 'The authorization decision could not be completed.');
      setSubmitting(false);
    }
  };

  if (!initialized || !isAuthenticated || (!request && !error)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-200">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-400" aria-label="Loading authorization request" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4 py-10 text-slate-100">
      <main className="w-full max-w-xl rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl sm:p-8">
        <div className="flex items-start gap-4">
          <div className="rounded-xl bg-cyan-400/10 p-3 text-cyan-300">
            <ShieldCheck className="h-7 w-7" aria-hidden="true" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-cyan-300">LineSide secure connection</p>
            <h1 className="mt-1 text-2xl font-semibold">Connect {request?.client_name || 'your personal agent'}?</h1>
            <p className="mt-2 text-sm leading-6 text-slate-400">
              This connection receives only the LineSide tool calls and results it requests. LineSide does not receive your unrelated ChatGPT or Claude conversation.
            </p>
          </div>
        </div>

        {request ? (
          <section className="mt-6 rounded-xl border border-slate-800 bg-slate-950/60 p-4">
            <label className="mb-4 block text-sm">Agent identity
              <select value={agentId} onChange={event => setAgentId(event.target.value)} disabled={submitting || !!request.agent_id}
                className="mt-2 block w-full rounded-lg border border-slate-700 bg-slate-900 p-2">
                <option value="" disabled>Select an Agent you own to reconnect</option>
                {(request.agents || []).map(agent => <option key={agent.id} value={agent.id}>Reconnect {agent.display_name || agent.name}</option>)}
              </select>
            </label>
            <p className="mb-4 text-sm text-slate-400">{request.agent_id
              ? 'This connection URL identifies the selected Agent. Authorizing again preserves its identity, permissions and history.'
              : 'Choose the Agent this assistant should use. Authorizing again preserves its identity, permissions and history.'}</p>
            {!request.agent_id && <p className="mb-4 text-sm text-slate-400">Need a new Agent? <a href="/app/mcp" target="_blank" rel="noreferrer" className="text-cyan-300 underline">Create one on the AI connections page</a>, then use its connection URL in your assistant.</p>}
            <h2 className="text-sm font-semibold text-slate-200">Requested access</h2>
            <ul className="mt-3 space-y-3">
              {(request.scopes || []).map((scope) => (
                <li key={scope} className="flex gap-2 text-sm text-slate-300">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" aria-hidden="true" />
                  <span>{SCOPE_LABELS[scope] || scope}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs leading-5 text-slate-500">
              Your live LineSide role, tenant membership, Agent permissions, skill permissions, and resource access are checked again for every call. Publication follows your connection’s approval mode.
            </p>
          </section>
        ) : null}

        {error ? (
          <div className="mt-5 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>
        ) : null}

        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            disabled={submitting || !request}
            onClick={() => decide(false)}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-700 px-4 py-2.5 text-sm font-semibold text-slate-200 hover:bg-slate-800 disabled:opacity-50"
          >
            <X className="h-4 w-4" aria-hidden="true" /> Deny
          </button>
          <button
            type="button"
            disabled={submitting || !request || !agentId}
            onClick={() => decide(true)}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-cyan-400 px-4 py-2.5 text-sm font-semibold text-slate-950 hover:bg-cyan-300 disabled:opacity-50"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            Allow connection
          </button>
        </div>
      </main>
    </div>
  );
};

export default McpAuthorizationPage;
